/* import __COLOCATED_TEMPLATE__ from './fin-workflows-empty-state.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import {
  CONVERSATION_STARTED_TARGET,
  NEW_CONVERSATION_TARGET,
} from 'embercom/lib/operator/custom-bots/constants';
import type Store from '@ember-data/store';
import type AIAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { tracked } from '@glimmer/tracking';
import type WorkflowCreationService from 'embercom/services/workflow-creation-service';
import { type ChatChannel } from 'embercom/services/workflow-creation-service';
import { action } from '@ember/object';

interface Signature {
  Element: HTMLDivElement;
  Args: {};
}

export default class FinWorkflowsEmptyState extends Component<Signature> {
  @service declare store: Store;
  @service declare aiAgentSetupService: AIAgentSetupService;
  @service declare workflowCreationService: WorkflowCreationService;

  @tracked selectedChatChannel: ChatChannel = 'messenger';

  get channelType() {
    return this.aiAgentSetupService.channelType;
  }

  get workflowTemplate() {
    if (this.channelType === 'email') {
      return this.workflowCreationService.finWorkflowTemplateForEmail;
    } else {
      return this.workflowCreationService.finWorkflowTemplateForChat[this.selectedChatChannel];
    }
  }

  get workflowTemplateContentObject() {
    if (!this.workflowTemplate) {
      return;
    }
    return this.workflowTemplate?.ruleset?.rulesetLinks.firstObject?.object;
  }

  @action
  createWorkflow() {
    if (!this.workflowTemplate) {
      return;
    }
    return this.workflowCreationService.createWorkflowFromTemplate(this.workflowTemplate);
  }

  @action
  createWorkflowFromScratch() {
    if (this.channelType === 'email') {
      return this.workflowCreationService.createWorkflowFromScratch({
        target: CONVERSATION_STARTED_TARGET,
        targetChannels: ['email'],
      });
    }

    switch (this.selectedChatChannel) {
      case 'messenger':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: NEW_CONVERSATION_TARGET,
          targetChannels: ['web', 'android', 'ios'],
        });
      case 'whatsapp':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['whatsapp'],
        });
      case 'slack':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['slack'],
        });
      case 'facebook':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['facebook'],
        });
      case 'instagram':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['instagram'],
        });
      case 'sms':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['sms'],
        });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::FinWorkflowsEmptyState': typeof FinWorkflowsEmptyState;
    'operator/fin/setup/fin-workflows-empty-state': typeof FinWorkflowsEmptyState;
  }
}
